import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { v4 as uuid4 } from 'uuid';
import TextBlock from '@component/common/block/text-block';
import { ETheme } from '@datatype/type';
import * as color from '@constant/color';
import * as sizeToken from '@constant/size';
import type { IHandwriting } from './type';

interface IBaseProps {
  initialSize?: IHandwriting['canvas'];
  placeholder?: string;
  width?: string;
  height?: string;
  className?: string;
  theme?: ETheme;
  size?: 'small' | 'medium' | 'large';
  preserveAspectRatio?: string;
}
type IProps = IBaseProps &
  (
    | { type: 'write'; value?: IHandwriting; onChange: (value: IHandwriting) => void }
    | { type: 'read'; value: IHandwriting; onChange?: never }
  );

const createDesign = (props: IProps) => {
  const createSize = () => ({
    strokeWidth:
      props.size === 'small'
        ? sizeToken.DESIGN_SYSTEM_HANDWRITING_SMALL_STROKE_WIDTH
        : props.size === 'large'
          ? sizeToken.DESIGN_SYSTEM_HANDWRITING_LARGE_STROKE_WIDTH
          : sizeToken.DESIGN_SYSTEM_HANDWRITING_MEDIUM_STROKE_WIDTH,
  });
  const createTheme = () => ({
    color: props.theme === ETheme.CYAN ? color.CYAN_2 : props.theme === ETheme.BROWN ? color.BROWN_1 : color.BLACK_2,
  });
  const canvas = props.value?.canvas ?? props.initialSize;
  return {
    ...createSize(),
    ...createTheme(),
    width: props.width,
    height: props.height,
    writable: props.type === 'write',
    aspectRatio: `${canvas?.width ?? 16} / ${canvas?.height ?? 9}`,
  };
};
const Wrapper = styled.div<{ $design: ReturnType<typeof createDesign> }>`
  position: relative;
  width: ${({ $design }) => $design.width || '100%'};
  height: ${({ $design }) => $design.height || 'auto'};
  aspect-ratio: ${({ $design }) => $design.aspectRatio};
  background-color: ${({ $design }) => ($design.writable ? color.WHITE_1 : 'transparent')};
  border: ${({ $design }) => ($design.writable ? `${sizeToken.BORDER_WIDTH_1} solid ${color.GRAY_2}` : 'none')};
  border-radius: ${sizeToken.BORDER_RADIUS_2};
  overflow: hidden;
`;
const Canvas = styled.svg<{ $writable: boolean }>`
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  touch-action: ${({ $writable }) => ($writable ? 'none' : 'auto')};
`;
const Placeholder = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  display: flex;
  width: 100%;
  justify-content: center;
  pointer-events: none;
  transform: translateY(-50%);
`;

const Handwriting: React.FC<IProps> = (props) => {
  const { type, value, placeholder, onChange, className, preserveAspectRatio } = props;
  const design = createDesign(props);
  const canvas = value?.canvas;
  const stroke = value?.stroke ?? [];
  const svgRef = useRef<SVGSVGElement>(null);
  const pointRef = useRef<IHandwriting['stroke'][number]['point'][number][]>([]);
  const [draftPoint, setDraftPoint] = useState<IHandwriting['stroke'][number]['point'][number][]>([]);
  const [measuredCanvas, setMeasuredCanvas] = useState<IHandwriting['canvas'] | null>(null);
  const logicalCanvas = canvas ?? measuredCanvas;

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || canvas || type !== 'write') return;
    const measure = () => {
      if (pointRef.current.length > 0) return;
      if (svg.clientWidth > 0 && svg.clientHeight > 0) {
        const width = svg.clientWidth;
        const height = svg.clientHeight;
        setMeasuredCanvas((previous) =>
          previous?.width === width && previous.height === height ? previous : { width, height },
        );
      }
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(svg);
    return () => observer.disconnect();
  }, [canvas, type]);

  const getPoint = (
    event: React.PointerEvent<SVGSVGElement>,
  ): IHandwriting['stroke'][number]['point'][number] | null => {
    if (!logicalCanvas) return null;
    const svg = event.currentTarget;
    const matrix = svg.getScreenCTM();
    if (!matrix) return null;
    const point = svg.createSVGPoint();
    point.x = event.clientX;
    point.y = event.clientY;
    const local = point.matrixTransform(matrix.inverse());
    return {
      x: Math.min(logicalCanvas.width, Math.max(0, local.x)),
      y: Math.min(logicalCanvas.height, Math.max(0, local.y)),
    };
  };

  const resetDraft = () => {
    pointRef.current = [];
    setDraftPoint([]);
  };

  const finish = (event: React.PointerEvent<SVGSVGElement>) => {
    if (!pointRef.current.length || !logicalCanvas) return;
    const point = getPoint(event);
    if (point && type === 'write')
      onChange({ stroke: [...stroke, { point: [...pointRef.current, point] }], canvas: logicalCanvas });
    resetDraft();
  };

  const StrokeItems = [...stroke, ...(draftPoint.length ? [{ point: draftPoint }] : [])].map(({ point }) => (
    <polyline
      key={uuid4()}
      points={point.map(({ x, y }) => `${x},${y}`).join(' ')}
      fill='none'
      stroke={design.color}
      strokeWidth={design.strokeWidth}
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  ));
  return (
    <Wrapper className={className} $design={design}>
      {type === 'write' && stroke.length === 0 && draftPoint.length === 0 && (
        <Placeholder>
          <TextBlock
            text={placeholder ?? '손글씨로 입력하세요.'}
            design={{ text: { size: 'medium', color: color.GRAY_1 } }}
          />
        </Placeholder>
      )}
      <Canvas
        ref={svgRef}
        $writable={type === 'write'}
        viewBox={logicalCanvas ? `0 0 ${logicalCanvas.width} ${logicalCanvas.height}` : undefined}
        preserveAspectRatio={preserveAspectRatio ?? 'xMidYMid meet'}
        onPointerDown={
          type === 'write'
            ? (event) => {
                if (pointRef.current.length) return;
                event.preventDefault();
                const point = getPoint(event);
                if (!point) return;
                pointRef.current = [point];
                setDraftPoint(pointRef.current);
                event.currentTarget.setPointerCapture(event.pointerId);
              }
            : undefined
        }
        onPointerMove={
          type === 'write'
            ? (event) => {
                if (!pointRef.current.length) return;
                const point = getPoint(event);
                if (!point) return;
                pointRef.current = [...pointRef.current, point];
                setDraftPoint(pointRef.current);
              }
            : undefined
        }
        onPointerUp={type === 'write' ? finish : undefined}
        onPointerCancel={type === 'write' ? resetDraft : undefined}
        onLostPointerCapture={type === 'write' ? resetDraft : undefined}>
        {StrokeItems}
      </Canvas>
    </Wrapper>
  );
};
export default Handwriting;
